import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAgent, useUpdateAgent } from '@/hooks/use-agents';
import { Button } from '@/components/ui/button';
import { AdminHeader } from '@/components/admin-header';
import { PostTitleSection } from '@/components/post-title-section';
import { RichTextEditorSection } from '@/components/rich-text-editor-section';
import { ExcerptSection } from '@/components/excerpt-section';
import { SEOSection } from '@/components/seo-section';
import { PublishSettingsSection } from '@/components/publish-settings-section';
import { FeaturedImageSection } from '@/components/featured-image-section';
import { CategoriesTagsSection } from '@/components/categories-tags-section';
import { AgentFormatSection, type AgentFormat } from '@/components/agent-format-section';
import { Input } from '@/components/ui/input';
import { Globe } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/agents/$id')({
  component: AgentEditPage,
});

type AgentModerationStatus = "draft" | "pending" | "published" | "unpublished" | "rejected";

type ProviderCountryOption = "vietnam" | "international";

const normalizeProviderCountry = (value?: string | null): ProviderCountryOption | "" => {
  if (!value) {
    return "";
  }

  const normalized = value.trim().toLowerCase();
  if (["việt nam", "viet nam", "vietnam", "vn", "vi"].includes(normalized)) {
    return "vietnam";
  }

  if (["quốc tế", "quoc te", "international", "global", "worldwide", "int"].includes(normalized)) {
    return "international";
  }

  return "";
};

const providerCountryToApiValue = (value: ProviderCountryOption | ""): string | undefined => {
  if (value === "vietnam") {
    return "Việt Nam";
  }
  if (value === "international") {
    return "Quốc tế";
  }
  return undefined;
};

function AgentEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: agent, isLoading, error } = useAgent(id);
  const updateAgent = useUpdateAgent();

  // Form state
  const [name, setName] = React.useState(agent?.name || "");
  const [code, setCode] = React.useState(agent?.code || "");
  const [description, setDescription] = React.useState(agent?.description || "");
  const [excerpt, setExcerpt] = React.useState(agent?.excerpt || "");
  const [moderationStatus, setModerationStatus] = React.useState<AgentModerationStatus>(
    (agent?.moderationStatus as AgentModerationStatus) ?? "pending"
  );
  const [visibility, setVisibility] = React.useState(agent?.visibility ?? "public");
  const [categories, setCategories] = React.useState<string[]>(agent?.categories ?? []);
  const [tags, setTags] = React.useState<string[]>(agent?.tags ?? []);
  const [provider, setProvider] = React.useState(agent?.provider || "");
  const [providerCountry, setProviderCountry] = React.useState<ProviderCountryOption | "">(
    normalizeProviderCountry(agent?.provider_country)
  );
  const [logo, setLogo] = React.useState(agent?.logo ?? "");
  const [pictures, setPictures] = React.useState<string[]>(agent?.pictures ?? []);
  const [videos, setVideos] = React.useState<Array<{
    title: string;
    url: string;
    thumbnail?: string;
    duration?: string;
    views?: string;
    channel?: string;
  }>>(agent?.videos ?? []);
  const [seoTitle, setSeoTitle] = React.useState(agent?.seo_title || "");
  const [seoDescription, setSeoDescription] = React.useState(agent?.seo_description || "");
  const [capabilities, setCapabilities] = React.useState<string[]>(agent?.capabilities ?? []);
  const [deployments, setDeployments] = React.useState<string[]>(agent?.deployments ?? []);
  const [agentFormat, setAgentFormat] = React.useState<AgentFormat>("standard");

  // UI state
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (agent) {
      setName(agent.name);
      const agentCode = agent.code || "";
      setCode(agentCode);
      
      // Auto-generate code from name if code doesn't exist
      if (!agentCode && agent.name) {
        const generatedCode = agent.name
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        setCode(generatedCode);
      }
      
      setDescription(agent.description || "");
      setExcerpt(agent.excerpt || "");
      setModerationStatus((agent.moderationStatus as AgentModerationStatus) ?? "pending");
      setVisibility(agent.visibility ?? "public");
      setCategories(agent.categories ?? []);
      setTags(agent.tags ?? []);
      setProvider(agent.provider || "");
      setProviderCountry(normalizeProviderCountry(agent.provider_country));
      setLogo(agent.logo ?? "");
      setPictures(agent.pictures ?? []);
      setVideos(agent.videos ?? []);
      setSeoTitle(agent.seo_title || "");
      setSeoDescription(agent.seo_description || "");
      setCapabilities(agent.capabilities ?? []);
      setDeployments(agent.deployments ?? []);
    }
  }, [agent]);

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async (targetStatus?: "draft" | "published") => {
    // Validation
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = "Tên agent là bắt buộc";
    }
    if (!provider.trim()) {
      newErrors.provider = "Nhà cung cấp là bắt buộc";
    }
    if (!description.trim()) {
      newErrors.description = "Mô tả là bắt buộc";
    }
    if (description.trim().length < 10) {
      newErrors.description = "Mô tả phải có ít nhất 10 ký tự";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSaveMessage(null);

    try {
      // If targetStatus is provided, use it; otherwise keep current status
      const finalStatus = targetStatus || moderationStatus;

      const payload = {
        name: name.trim(),
        code: code.trim() || undefined,
        provider: provider.trim(),
        provider_country: providerCountryToApiValue(providerCountry),
        description: description.trim(),
        excerpt: excerpt.trim() || undefined,
        moderationStatus: finalStatus,
        visibility,
        tags,
        categories,
        logo: logo || undefined,
        pictures,
        videos,
        capabilities,
        deployments,
        seo_title: seoTitle.trim() || undefined,
        seo_description: seoDescription.trim() || undefined,
      };

      const updatedAgent = await updateAgent.mutateAsync({ id, payload });

      // Update local state from response to ensure consistency
      if (updatedAgent) {
        if (updatedAgent.categories) {
          setCategories(updatedAgent.categories);
        }
        if (updatedAgent.moderationStatus) {
          setModerationStatus(updatedAgent.moderationStatus as AgentModerationStatus);
        }
      }

      setSaveMessage("✓ Đã lưu thành công!");
      setTimeout(() => {
        setSaveMessage(null);
        if (targetStatus === "published") {
          navigate({ to: "/agents" });
        }
      }, 2000);
    } catch (error) {
      const err = error as Error;
      setErrors({ submit: err.message || "Không thể lưu agent" });
    }
  };

  const handleSaveOnly = async () => {
    // Save without changing status - keep current moderation status
    await handleSave();
  };

  const handleSaveAndPublish = async () => {
    await handleSave("published");
  };

  const handleCodeSave = async () => {
    // Save only the code field
    try {
      const payload = {
        code: code.trim() || undefined,
      };

      await updateAgent.mutateAsync({ id, payload });
      setSaveMessage("✓ Đã lưu permalink thành công!");
      setTimeout(() => {
        setSaveMessage(null);
      }, 2000);
    } catch (error) {
      const err = error as Error;
      throw new Error(err.message || "Không thể lưu permalink");
    }
  };

  const handleUnpublish = async () => {
    // Validation
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = "Tên agent là bắt buộc";
    }
    if (!provider.trim()) {
      newErrors.provider = "Nhà cung cấp là bắt buộc";
    }
    if (!description.trim()) {
      newErrors.description = "Mô tả là bắt buộc";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSaveMessage(null);

    try {
      const payload = {
        name: name.trim(),
        code: code.trim() || undefined,
        provider: provider.trim(),
        provider_country: providerCountryToApiValue(providerCountry),
        description: description.trim(),
        excerpt: excerpt.trim() || undefined,
        moderationStatus: "unpublished" as const,
        visibility,
        tags,
        categories,
        logo: logo || undefined,
        pictures,
        videos,
        capabilities,
        deployments,
        seo_title: seoTitle.trim() || undefined,
        seo_description: seoDescription.trim() || undefined,
      };

      const updatedAgent = await updateAgent.mutateAsync({ id, payload });

      // Update local state from response
      if (updatedAgent) {
        if (updatedAgent.categories) {
          setCategories(updatedAgent.categories);
        }
        if (updatedAgent.moderationStatus) {
          setModerationStatus(updatedAgent.moderationStatus as AgentModerationStatus);
        }
      }

      setSaveMessage("✓ Đã unpublish thành công!");
      setTimeout(() => {
        setSaveMessage(null);
      }, 2000);
    } catch (error) {
      const err = error as Error;
      setErrors({ submit: err.message || "Không thể unpublish agent" });
    }
  };

  // Generate permalink: use code if exists, otherwise use generated code from name, fallback to id
  const permalinkCode = code.trim() || (name ? name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") : id);
  const agentUrl = `/agent/${permalinkCode}`;
  const permalink = agentUrl;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-muted-foreground">Đang tải agent...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600 font-medium">
            {error instanceof Error ? error.message : 'Lỗi khi tải agent'}
          </p>
        </div>
        <Link to="/agents">
          <Button variant="outline">Quay lại danh sách</Button>
        </Link>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-600 font-medium">Agent không tồn tại</p>
        </div>
        <Link to="/agents">
          <Button variant="outline">Quay lại danh sách</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header - Replaces main header */}
      <AdminHeader
        breadcrumbs={[
          { label: "Agents", href: "/agents" },
          { label: "Chỉnh sửa Agent" },
        ]}
        title="Chỉnh sửa Agent"
        onPreview={() => window.open(agentUrl, "_blank")}
        onSave={handleSaveOnly}
        onSaveAndPublish={handleSaveAndPublish}
        onUnpublish={handleUnpublish}
        isSaving={updateAgent.isPending}
        previewUrl={agentUrl}
        isPublished={moderationStatus === "published"}
      />

      {/* Error Banner */}
      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md mx-6 mt-6">
          <p className="text-sm text-red-600 font-medium">{errors.submit}</p>
        </div>
      )}

      {/* Success Message */}
      {saveMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md mx-6 mt-6">
          <p className="text-sm text-green-700 font-medium">{saveMessage}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Post Title Section */}
            <PostTitleSection
              value={name}
              onChange={(value) => {
                setName(value);
                clearError("name");
                // Auto-generate code from name if code is empty
                if (!code.trim()) {
                  const generatedCode = value
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "");
                  setCode(generatedCode);
                }
              }}
              permalink={permalink}
              code={agent?.code} // Only pass code if it exists in DB
              onCodeChange={setCode}
              onCodeSave={handleCodeSave}
              isCodeSaving={updateAgent.isPending}
              error={errors.name}
              required
            />

            {/* Rich Text Editor Section */}
            <RichTextEditorSection
              value={description}
              onChange={(value) => {
                setDescription(value);
                clearError("description");
              }}
              placeholder="Start writing your agent description..."
              label="Description"
              error={errors.description}
              required
            />

            {/* Excerpt Section */}
            <ExcerptSection
              value={excerpt}
              onChange={setExcerpt}
              placeholder="Write a brief summary of your agent..."
              helperText="Optional. This will be used in agent previews and meta descriptions."
            />

            {/* SEO Section */}
            <SEOSection
              metaTitle={seoTitle}
              metaDescription={seoDescription}
              focusKeyword=""
              onMetaTitleChange={setSeoTitle}
              onMetaDescriptionChange={setSeoDescription}
            />

            {/* Featured Image & Videos */}
            <FeaturedImageSection
              logo={logo}
              pictures={pictures}
              videos={videos}
              onLogoChange={setLogo}
              onPicturesChange={setPictures}
              onVideosChange={setVideos}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <PublishSettingsSection
              status={moderationStatus}
              visibility={visibility}
              onStatusChange={setModerationStatus}
              onVisibilityChange={setVisibility}
            />

            {/* Provider Field */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Provider</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nhà cung cấp <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={provider}
                    onChange={(e) => {
                      setProvider(e.target.value);
                      clearError("provider");
                    }}
                    placeholder="Nhà cung cấp..."
                    className={errors.provider ? "border-red-500" : ""}
                  />
                  {errors.provider && (
                    <p className="text-sm text-red-500 mt-1">{errors.provider}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Quốc gia nhà cung cấp
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className={`flex items-center cursor-pointer group p-3 rounded-lg border transition-colors ${
                      providerCountry === "vietnam" 
                        ? "border-primary bg-primary/5" 
                        : "border-gray-200 hover:border-primary hover:bg-primary/5"
                    }`}>
                      <input
                        type="radio"
                        name="provider-country"
                        value="vietnam"
                        checked={providerCountry === "vietnam"}
                        onChange={(e) => {
                          setProviderCountry(e.target.value as ProviderCountryOption);
                          clearError("provider_country");
                        }}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary focus:ring-2"
                      />
                      <div className="ml-3 flex items-center">
                        <span
                          className={`mr-2 flex-shrink-0 ${
                            providerCountry === "vietnam"
                              ? "text-primary"
                              : "text-gray-500 group-hover:text-primary"
                          }`}
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="24" height="16" fill="#DA020E"/>
                            <path d="M12 4.5L13.5 8.5L17.5 9.5L14.5 12.5L15.5 16.5L12 14L8.5 16.5L9.5 12.5L6.5 9.5L10.5 8.5L12 4.5Z" fill="#FFFF00"/>
                          </svg>
                        </span>
                        <span className="text-sm font-medium text-gray-700">Việt Nam</span>
                      </div>
                    </label>
                    <label className={`flex items-center cursor-pointer group p-3 rounded-lg border transition-colors ${
                      providerCountry === "international" 
                        ? "border-primary bg-primary/5" 
                        : "border-gray-200 hover:border-primary hover:bg-primary/5"
                    }`}>
                      <input
                        type="radio"
                        name="provider-country"
                        value="international"
                        checked={providerCountry === "international"}
                        onChange={(e) => {
                          setProviderCountry(e.target.value as ProviderCountryOption);
                          clearError("provider_country");
                        }}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary focus:ring-2"
                      />
                      <div className="ml-3 flex items-center">
                        <span
                          className={`mr-2 flex-shrink-0 ${
                            providerCountry === "international"
                              ? "text-primary"
                              : "text-gray-500 group-hover:text-primary"
                          }`}
                        >
                          <Globe className="w-5 h-5" />
                        </span>
                        <span className="text-sm font-medium text-gray-700">Quốc tế</span>
                      </div>
                    </label>
                  </div>
                  {errors.provider_country && (
                    <p className="text-sm text-red-500 mt-2">{errors.provider_country}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Categories & Tags */}
            <CategoriesTagsSection
              categories={categories}
              tags={tags}
              onCategoriesChange={setCategories}
              onTagsChange={setTags}
            />

            {/* Agent Format */}
            <AgentFormatSection
              value={agentFormat}
              onChange={setAgentFormat}
            />

            {/* Capabilities & Deployments */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Capabilities & Deployments</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Capabilities
                  </label>
                  <Input
                    value={capabilities.join(", ")}
                    onChange={(e) => {
                      const newCapabilities = e.target.value
                        .split(",")
                        .map((c) => c.trim())
                        .filter(Boolean);
                      setCapabilities(newCapabilities);
                    }}
                    placeholder="Chat, Coding, Data Analysis..."
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deployments
                  </label>
                  <Input
                    value={deployments.join(", ")}
                    onChange={(e) => {
                      const newDeployments = e.target.value
                        .split(",")
                        .map((d) => d.trim())
                        .filter(Boolean);
                      setDeployments(newDeployments);
                    }}
                    placeholder="Web App, API, Plugin..."
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}